import React from 'react';
import { cn } from '../../lib/utils';

export function WizardCard({
  children,
  className,
}: Readonly<{
  children: React.ReactNode;
  className?: string;
}>) {
  return (
    <div
      className={cn(
        // Layout wrapper only: no extra "card layer" inside wizard modals
        // (the modal itself already provides background/border).
        'space-y-4',
        className
      )}
    >
      {children}
    </div>
  );
}

export function WizardSection({
  title,
  children,
  className,
}: Readonly<{
  title: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}>) {
  return (
    <section className={cn('rounded-xl border bg-muted/20 p-4 shadow-sm transition-shadow duration-normal ease-standard motion-safe:hover:shadow-surface md:p-5', className)}>
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      {children}
    </section>
  );
}

export function WizardKVGrid({ children, className }: Readonly<{ children: React.ReactNode; className?: string }>) {
  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-[140px_1fr] gap-x-4 gap-y-2', className)}>{children}</div>
  );
}

export function WizardKVItem({
  label,
  children,
  className,
}: Readonly<{
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}>) {
  return (
    <>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={cn('text-sm', className)}>{children}</div>
    </>
  );
}

export function WizardPills({ children, className }: Readonly<{ children: React.ReactNode; className?: string }>) {
  return <div className={cn('flex flex-wrap gap-2', className)}>{children}</div>;
}

export function WizardPill({
  active = false,
  children,
  className,
}: Readonly<{
  active?: boolean;
  children: React.ReactNode;
  className?: string;
}>) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-fast ease-standard',
        active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
        className
      )}
    >
      {children}
    </span>
  );
}

export function WizardYesNoBadge({ yes, className }: Readonly<{ yes: boolean; className?: string }>) {
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-fast ease-standard',
        yes ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200',
        className
      )}
    >
      {yes ? 'wird versendet' : 'wird nicht versendet'}
    </span>
  );
}



export function WizardEmpfaenger({
  name,
  kanaele,
  className,
}: Readonly<{
  name: React.ReactNode;
  /** Die Kontaktwege in fester Reihenfolge — gefärbt, wenn sie genutzt werden. */
  kanaele: ReadonlyArray<{ label: string; aktiv: boolean }>;
  className?: string;
}>) {
  return (
    <div className={cn('grid grid-cols-1 items-center gap-2 md:grid-cols-[1fr_auto]', className)}>
      <div className="text-sm">{name}</div>
      <WizardPills className="md:justify-end">
        {kanaele.map((kanal) => (
          <WizardPill key={kanal.label} active={kanal.aktiv}>
            {kanal.label}
          </WizardPill>
        ))}
      </WizardPills>
    </div>
  );
}
